/**

 * Site Layout Preview 3D — layout-table proxy in memory + production Site3d scene.

 *

 * Canvas overlay only (sidebar + top toolbar stay usable). Close via Site3d ×.

 */

import React, { useCallback, useLayoutEffect } from "react";

import { MuiMaterials } from "../@index.js";

import * as global from "../data/global";

import { Site3d } from "./Site3d.js";

import {

    applyLayoutDraftToSiteForPreview3d,

    finalizeEditorLayoutForSite3dView,

    resetSiteLayoutRefs,

    restoreSiteLayout,

    snapshotSiteLayout,

} from "./layoutDraftToSite.js";

import { fitLayoutPreview3dCamera } from "./layoutPreviewView.js";



let activePreviewSnap = null;

let previewCameraFitTimers = [];



const clearPreviewCameraFitTimers = () => {

    previewCameraFitTimers.forEach((id) => clearTimeout(id));

    previewCameraFitTimers = [];

};



/** One-time initial camera fit (re-running causes choppy OrbitControls). */

export const tryLayoutPreview3dCameraFit = () => {

    const site = global.gw?.selectedSite;

    if (!site?.isLayoutPreview3d || site.layoutPreviewCameraFitDone) {

        return true;

    }

    const draft = site.layoutPreviewDraft;

    if (!draft || !site.orbitControls?.object) {

        return false;

    }

    const root = site.groupsRef?.[0]?.parent ?? site.siteRef?.[0];

    if (!root) {

        return false;

    }



    const el = site.canvasRef;

    const rect = el?.getBoundingClientRect?.();

    const viewport =

        rect?.width > 0 && rect?.height > 0

            ? { width: rect.width, height: rect.height }

            : undefined;

    const ok = fitLayoutPreview3dCamera(

        site,

        draft,

        viewport,

        site.layoutPreviewEditorView ?? null

    );

    if (ok) {

        site.layoutPreviewCameraFitDone = true;

        clearPreviewCameraFitTimers();

    }

    return ok;

};



const scheduleLayoutPreview3dCameraFit = (draft, editorView) => {

    clearPreviewCameraFitTimers();

    const site = global.gw?.selectedSite;

    if (site) {

        site.layoutPreviewDraft = draft;

        site.layoutPreviewEditorView = editorView;

        site.layoutPreviewCameraFitDone = false;

        delete site.layoutPreviewRootReset;

    }



    const poll = (attempt) => {

        if (tryLayoutPreview3dCameraFit()) {

            return;

        }

        if (attempt >= 40) {

            return;

        }

        previewCameraFitTimers.push(

            setTimeout(() => poll(attempt + 1), 50)

        );

    };

    poll(0);

};



export const mountPreviewProxyFromDraft = (draft, editorView = null) => {

    const site = global.gw?.selectedSite;

    if (!site?.siteGuid || !draft) return false;



    if (!activePreviewSnap) {

        activePreviewSnap = snapshotSiteLayout(site);

    }

    if (site.layoutCables?.length) {

        activePreviewSnap.layoutCables = site.layoutCables;

        activePreviewSnap.layoutSensors = site.layoutSensors;

    }

    if (site.layoutBins?.length) {

        activePreviewSnap.layoutBins = site.layoutBins;

    }



    delete site.isLayoutPreview;

    delete site.isLayoutPreviewSite;



    const ok = applyLayoutDraftToSiteForPreview3d(site, draft);

    if (!ok) return false;

    finalizeEditorLayoutForSite3dView(site, activePreviewSnap, {
        rebuildFromBts: true,
    });



    site.isLayoutPreview3d = true;

    site.isCablesOff = false;

    site.layoutPreviewWantsCameraMode = true;

    global.clearSiteBinLevels?.();



    if (activePreviewSnap?.isGradesOn !== undefined) {

        site.isGradesOn = false;

    }



    global.gw.siteLayoutPreview3dActive = true;

    scheduleLayoutPreview3dCameraFit(draft, editorView);

    return true;

};



export const unmountPreviewProxy = () => {

    clearPreviewCameraFitTimers();

    const site = global.gw?.selectedSite;

    const snap = activePreviewSnap;

    activePreviewSnap = null;

    delete global.gw.siteLayoutPreview3dActive;



    if (!site || !snap) return;



    restoreSiteLayout(site, snap);

    delete site.isLayoutPreview;

    delete site.isLayoutPreviewSite;

    delete site.isLayoutPreview3d;

    delete site.layoutPreviewEditorView;

    delete site.layoutPreviewDraft;

    delete site.layoutPreviewCameraFitDone;

    delete site.layoutPreviewRootReset;

    delete site.layoutPreviewWantsCameraMode;

    resetSiteLayoutRefs(site);



    try {

        global.initializeSiteLayout?.();

    } catch {

        /* restore prior layout* from snap */

    }

};



function PreviewSite3dScene({ onClose }) {

    const handleClose = useCallback(() => {

        unmountPreviewProxy();

        onClose?.();

    }, [onClose]);



    useLayoutEffect(() => {

        global.gw.siteLayoutPreview3dOnClose = handleClose;

        return () => {

            delete global.gw.siteLayoutPreview3dOnClose;

        };

    }, [handleClose]);



    return (

        <Site3d

            layoutPreviewMode

            onLayoutPreviewClose={handleClose}

            isHistoryLoading={false}

        />

    );

}



/** Full-bleed preview on the layout canvas (no extra preview header bar). */

export default function PreviewSite3dPanel({ open, draft, editorView, onClose }) {

    const draftBinCount = (draft?.groups || []).reduce(

        (n, g) => n + (g.bins?.length || 0),

        0

    );



    const handleClose = useCallback(() => {

        unmountPreviewProxy();

        onClose?.();

    }, [onClose]);



    useLayoutEffect(() => {

        if (!open || !draft) return undefined;

        mountPreviewProxyFromDraft(draft, editorView);

        return () => unmountPreviewProxy();

    }, [open, draft, editorView]);



    if (!open || !draft) return null;



    const binCount = global.gw?.selectedSite?.layoutBins?.length ?? draftBinCount;

    if (binCount === 0) {

        return (

            <MuiMaterials.Box

                sx={{

                    position: "absolute",

                    inset: 0,

                    zIndex: 30,

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    bgcolor: "rgba(0,0,0,0.5)",

                }}

            >

                <MuiMaterials.Typography sx={{ color: "#bbb", p: 3 }}>

                    No bins in the draft. Place bins on the layout image and try again.

                </MuiMaterials.Typography>

            </MuiMaterials.Box>

        );

    }



    return (

        <MuiMaterials.Box

            sx={{

                position: "absolute",

                inset: 0,

                zIndex: 30,

                overflow: "hidden",

            }}

        >

            <PreviewSite3dScene onClose={handleClose} />

        </MuiMaterials.Box>

    );

}


